// Connects an Editor to the server: local ops go out batched once per
// animation frame; server messages feed SyncState (which updates the store)
// and Presence. Reconnects resume from lastSeq and re-send unacked ops.
import type { Op, ServerOp } from '../../shared/types.ts';
import { isColumn } from '../../shared/columns.ts';
import { columnParents } from '../model/columns.ts';
import { opObjectId } from '../../shared/ops.ts';
import type { KickReason, ServerMessage } from '../../shared/protocol.ts';
import type { Editor } from '../editor.ts';
import { SyncState, type OutgoingOp } from './model.ts';
import { Presence } from './presence.ts';
import { ReconnectingSocket, type SocketStatus } from './socket.ts';

const PRESENCE_MS = 40;       // ≤ 25 Hz (spec §6.3)
const OPS_PER_MESSAGE = 500;

export type SyncStatus = 'connecting' | 'saved' | 'saving' | 'reconnecting' | 'closed';

export interface BoardSyncOptions {
  latencyMs?: number;
  onFirstLoad?: () => void;
  onStatus?: (status: SyncStatus) => void;
  onKicked?: (reason: KickReason) => void;
  onReject?: (reason: string) => void;
  /** Watching through a public link (spec §17.2): joins by the link, read-only, and sends no presence. */
  publicToken?: string;
}

export class BoardSync {
  readonly state: SyncState;
  readonly presence = new Presence();
  private readonly ed: Editor;
  private readonly socket: ReconnectingSocket;
  private readonly opts: BoardSyncOptions;
  private joined = false;
  private flushScheduled = false;
  private status: SyncStatus = 'connecting';
  private presenceTimer = 0;
  private presenceQueued: { cursor?: [number, number] | null; selection?: string[] } = {};
  private lastPresenceAt = 0;
  private readonly userId: string;
  /** Who sent the latest op on each card (see repairCards). */
  private readonly lastAuthor = new Map<string, string>();
  /** Who sent the latest op on each column or one of its cards (see repairColumns). */
  private readonly lastColumnAuthor = new Map<string, string>();

  constructor(editor: Editor, boardId: string, userId: string, opts: BoardSyncOptions = {}) {
    this.ed = editor;
    this.opts = opts;
    this.userId = userId;
    this.state = new SyncState(editor.store, userId);
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const query = opts.publicToken ? `?public=${encodeURIComponent(opts.publicToken)}` : '';
    this.socket = new ReconnectingSocket(`${proto}//${location.host}/ws${query}`, opts.latencyMs ?? 0);
    // A public viewer joins by the link (the server knows which board it's for).
    const joinId = opts.publicToken ?? boardId;
    this.socket.onOpen = () => this.socket.send({ type: 'join', boardId: joinId, lastSeq: this.state.lastSeq >= 0 ? this.state.lastSeq : undefined });
    this.socket.onMessage = (m) => this.handle(m);
    this.socket.onStatus = (s) => this.socketStatus(s);

    editor.onLocalOps = (ops) => this.local(ops);
    editor.presence = this.presence;
    this.presence.onChange = () => editor.renderer.invalidateInteractive();
    editor.events.on('selection', () => this.queuePresence({ selection: [...editor.selection].slice(0, 1000) }));
    const canvas = editor.layers.interactiveCanvas;
    canvas.addEventListener('pointermove', () => { if (editor.pointer) this.queuePresence({ cursor: editor.pointer }); });
    canvas.addEventListener('pointerleave', () => this.queuePresence({ cursor: null }));
  }

  start(): void {
    this.socket.connect();
  }

  stop(): void {
    this.socket.close();
  }

  /** Unsynced local changes exist (warn before leaving). */
  get dirty(): boolean {
    return this.state.pendingCount > 0;
  }

  get info(): { seq: number; pending: number; status: SyncStatus } {
    return { seq: this.state.lastSeq, pending: this.state.pendingCount, status: this.status };
  }

  /** Development aid: drop the connection to exercise resume. */
  reconnect(): void {
    this.socket.reconnectNow();
  }

  private local(ops: readonly Op[]): void {
    this.state.local(ops);
    this.updateStatus();
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    // Once per animation frame (spec §6.1); a hidden tab has no frames.
    if (document.hidden) setTimeout(() => this.flush(), 50);
    else requestAnimationFrame(() => this.flush());
  }

  private flush(): void {
    this.flushScheduled = false;
    if (this.joined) this.sendOps(this.state.takeOutbox());
  }

  private sendOps(ops: OutgoingOp[]): void {
    for (let i = 0; i < ops.length; i += OPS_PER_MESSAGE) {
      if (!this.socket.send({ type: 'ops', ops: ops.slice(i, i + OPS_PER_MESSAGE) })) return; // re-sent after reconnect
    }
  }

  private handle(m: ServerMessage): void {
    switch (m.type) {
      case 'init': {
        const first = this.state.lastSeq < 0;
        this.state.init(m.snapshot, m.ops, m.seq);
        if (first) this.ed.history.clear();
        this.joined = true;
        this.presence.setAll(m.members);
        this.sendOps(this.state.resendAll());
        if (first) this.opts.onFirstLoad?.();
        this.sendPresenceNow();
        break;
      }
      case 'resume':
        this.state.receive(m.ops);
        this.repairCards(m.ops);
        this.repairColumns(m.ops);
        this.joined = true;
        this.presence.setAll(m.members);
        this.sendOps(this.state.resendAll());
        this.sendPresenceNow();
        break;
      case 'ops':
        this.state.receive(m.ops);
        this.repairCards(m.ops);
        this.repairColumns(m.ops);
        break;
      case 'reject':
        this.state.reject(m.clientOpIds);
        this.opts.onReject?.(m.reason);
        break;
      case 'presence':
        this.presence.update(m);
        break;
      case 'peer-left':
        this.presence.remove(m.userId);
        break;
      case 'kicked':
        this.joined = false;
        this.socket.close();
        this.presence.setAll([]);
        this.opts.onKicked?.(m.reason);
        break;
      case 'pong':
        break;
    }
    this.updateStatus();
  }

  /**
   * A card's height follows its content, but two people changing a card at
   * once each send a height for what they saw. Whoever sent the card's latest
   * op re-fits it once their ops are confirmed; everyone sees the same op
   * order, so exactly one client does this.
   */
  private repairCards(ops: readonly ServerOp[]): void {
    const cards = new Set<string>();
    for (const sop of ops) {
      const id = opObjectId(sop.op);
      if (this.ed.store.get(id)?.type !== 'card') {
        this.lastAuthor.delete(id);
        continue;
      }
      this.lastAuthor.set(id, sop.userId);
      cards.add(id);
    }
    const mine = [...cards].filter((id) => this.lastAuthor.get(id) === this.userId && !this.state.hasPending(id));
    if (mine.length) this.ed.repairCardHeights(mine);
  }

  /**
   * Columns stack their cards (spec §13.6), but two people changing one at
   * once each lay it out for what they saw. As with card heights, whoever sent
   * the latest op on the column or one of its cards re-stacks it once nothing
   * of theirs there is pending; everyone sees the same order, so one client does.
   */
  private repairColumns(ops: readonly ServerOp[]): void {
    const cols = this.ed.columnList();
    if (!cols.length) return;
    const parent = columnParents(cols);
    const touched = new Set<string>();
    for (const sop of ops) {
      const id = opObjectId(sop.op);
      const col = isColumn(this.ed.store.get(id)) ? id : parent.get(id);
      if (!col) continue;
      this.lastColumnAuthor.set(col, sop.userId);
      touched.add(col);
    }
    const quiet = (col: string) => {
      const c = this.ed.store.get(col);
      return isColumn(c) && !this.state.hasPending(col) && !c.children.some((id) => this.state.hasPending(id));
    };
    const mine = [...touched].filter((col) => this.lastColumnAuthor.get(col) === this.userId && quiet(col));
    if (mine.length) this.ed.repairColumns(mine);
  }

  private socketStatus(s: SocketStatus): void {
    if (s !== 'open') this.joined = false;
    this.updateStatus();
  }

  private updateStatus(): void {
    const s: SyncStatus = this.socket.status === 'closed' ? 'closed'
      : this.socket.status === 'reconnecting' ? 'reconnecting'
      : !this.joined ? (this.state.lastSeq < 0 ? 'connecting' : 'reconnecting')
      : this.state.pendingCount ? 'saving' : 'saved';
    if (s === this.status) return;
    this.status = s;
    this.opts.onStatus?.(s);
  }

  private queuePresence(p: { cursor?: [number, number] | null; selection?: string[] }): void {
    if (this.opts.publicToken) return; // the public aren't shown to anyone
    Object.assign(this.presenceQueued, p);
    if (this.presenceTimer) return;
    const wait = Math.max(0, this.lastPresenceAt + PRESENCE_MS - performance.now());
    this.presenceTimer = window.setTimeout(() => this.sendPresenceNow(), wait);
  }

  private sendPresenceNow(): void {
    clearTimeout(this.presenceTimer);
    this.presenceTimer = 0;
    if (!this.joined) return;
    const p = this.presenceQueued;
    if (p.cursor === undefined && p.selection === undefined) return;
    this.presenceQueued = {};
    this.lastPresenceAt = performance.now();
    this.socket.send({ type: 'presence', ...p });
  }
}
